/**
 * IdleSmilePresets.js
 *
 * Pure-data preset library + per-character assignments for the idle
 * smile system (see IdleSmileController.js). Presets are ARKit 52
 * blendshape weight maps tuned to subtle, idle-friendly intensities.
 *
 * Three reaction-strength presets from the original reference list
 * (Wide Friendly Smile, Excited Greeting Smile, Laugh-Start Smile)
 * are intentionally excluded — they're too loud for a quiet idle
 * drift and would look like glitches.
 */

export const IDLE_SMILE_PRESETS = {
  neutral_alive: {
    displayName: "Neutral Alive Face",
    weights: {
      jawOpen: 0.01,
      browInnerUp: 0.06, // was 0.01 — much more present
      browOuterUpLeft: 0.04, // NEW — lifts whole brow arch
      browOuterUpRight: 0.04, // NEW
      eyeWideLeft: 0.06, // NEW — subtle alertness
      eyeWideRight: 0.06, // NEW
      eyeSquintLeft: 0.02,
      eyeSquintRight: 0.02,
      cheekSquintLeft: 0.02,
      cheekSquintRight: 0.02,
      mouthSmileLeft: 0.025,
      mouthSmileRight: 0.025,
    },
  },
  soft_friendly: {
    displayName: "Soft Friendly Smile",
    weights: {
      jawOpen: 0.02,
      browInnerUp: 0.09, // was 0.012
      browOuterUpLeft: 0.06, // NEW
      browOuterUpRight: 0.06, // NEW
      eyeSquintLeft: 0.12, // was 0.02
      eyeSquintRight: 0.12, // was 0.02
      cheekSquintLeft: 0.18, // was 0.05
      cheekSquintRight: 0.18, // was 0.05
      mouthSmileLeft: 0.15,
      mouthSmileRight: 0.15,
      mouthDimpleLeft: 0.04,
      mouthDimpleRight: 0.04,
    },
  },
  warm_teacher: {
    displayName: "Warm Teacher Smile",
    weights: {
      jawOpen: 0.025,
      browInnerUp: 0.12, // was 0.015
      browOuterUpLeft: 0.09, // NEW — key Duchenne marker
      browOuterUpRight: 0.09, // NEW
      eyeSquintLeft: 0.22, // was 0.035
      eyeSquintRight: 0.22, // was 0.035
      cheekSquintLeft: 0.28, // was 0.08
      cheekSquintRight: 0.28, // was 0.08
      mouthSmileLeft: 0.2,
      mouthSmileRight: 0.2,
      mouthDimpleLeft: 0.06,
      mouthDimpleRight: 0.06,
    },
  },
  genuine_happy: {
    displayName: "Genuine Happy Smile",
    weights: {
      jawOpen: 0.04,
      browInnerUp: 0.15, // NEW addition
      browOuterUpLeft: 0.14, // NEW
      browOuterUpRight: 0.14, // NEW
      eyeSquintLeft: 0.32, // was 0.06
      eyeSquintRight: 0.32, // was 0.06
      cheekSquintLeft: 0.38, // was 0.13
      cheekSquintRight: 0.38, // was 0.13
      mouthSmileLeft: 0.26,
      mouthSmileRight: 0.26,
      mouthDimpleLeft: 0.085,
      mouthDimpleRight: 0.085,
    },
  },
  wise_scholar: {
    displayName: "Wise Scholar Smile",
    weights: {
      jawOpen: 0.01,
      browInnerUp: 0.05, // was 0.008
      browDownLeft: 0.04, // NEW — thoughtful furrow
      browDownRight: 0.04, // NEW
      eyeSquintLeft: 0.08, // was 0.012
      eyeSquintRight: 0.08, // was 0.012
      noseSneerLeft: 0.03, // NEW — adds subtle character
      noseSneerRight: 0.03, // NEW
      cheekSquintLeft: 0.07,
      cheekSquintRight: 0.07,
      mouthSmileLeft: 0.08,
      mouthSmileRight: 0.08,
    },
  },
  elegant_soft: {
    displayName: "Elegant Soft Smile",
    weights: {
      jawOpen: 0.012,
      browInnerUp: 0.13, // was 0.025 — this alone transforms the look
      browOuterUpLeft: 0.05, // NEW
      browOuterUpRight: 0.05, // NEW
      eyeSquintLeft: 0.07, // was 0.018
      eyeSquintRight: 0.07, // was 0.018
      cheekSquintLeft: 0.14, // was 0.045
      cheekSquintRight: 0.14, // was 0.045
      mouthSmileLeft: 0.14,
      mouthSmileRight: 0.14,
      mouthDimpleLeft: 0.03,
      mouthDimpleRight: 0.03,
    },
  },
};

/** Presets considered "subtle" — biased toward by the variation loop. */
export const SUBTLE_PRESET_KEYS = new Set([
  "neutral_alive",
  "tiny_natural",
  "polite",
  "wise_scholar",
  "elegant_soft",
  "closed_mouth",
]);

/** Default preset list when a character has no explicit assignment. */
export const DEFAULT_IDLE_SMILE_PRESETS = ["neutral_alive", "tiny_natural"];

/**
 * Per-character preset assignments.
 *
 * Keys are model UUIDs as used by `ModelViewer.currentModelKey`
 * (sourced from `character_mapping.py` / `CharacterPoseConfig.js` —
 * NOT from the display roster in `static/data/characters.json`,
 * which uses different IDs for several characters).
 */
export const CHARACTER_SMILE_ASSIGNMENTS = {
  // Felix Klein
  "ec6c5b20-8ac6-42bd-b291-f248cb3f0ebc": [
    "warm_teacher",
    "wise_scholar",
    "neutral_alive",
  ],
  // François Viète
  "8f67d579-41c2-456d-900b-fccbcbf46bd3": [
    "wise_scholar",
    "warm_teacher",
    "elegant_soft",
  ],
  // Claude Shannon
  "2db1d398-0161-46cd-8aff-53008d29a9dc": [
    "soft_friendly",
    "warm_teacher",
    "genuine_happy",
  ],
  // Marcus Aurelius
  "e97568c5-1daa-4a95-b4ae-0fe374c6b083": [
    "wise_scholar",
    "neutral_alive",
    "soft_friendly",
  ],
  // Aristotle
  "c4b9ac58-a66a-451e-b035-afabd8be50b9": [
    "wise_scholar",
    "warm_teacher",
    "neutral_alive",
  ],
  // Archimedes
  "d13b5a81-ed77-4a25-8a01-a76a0165b349": [
    "genuine_happy",
    "warm_teacher",
    "wise_scholar",
  ],
  // Alessandro Volta
  "ece0ab37-37c1-4ead-8222-fbb5b185dbe2": [
    "warm_teacher",
    "wise_scholar",
    "soft_friendly",
  ],
  // Eratosthenes
  "c73fed54-81e3-46ec-9d7c-3d11d05b142d": [
    "warm_teacher",
    "genuine_happy",
    "wise_scholar",
  ],
  // Thomas Bayes
  "3140d634-c7ca-4bda-af36-e1b49978b201": [
    "wise_scholar",
    "soft_friendly",
    "warm_teacher",
  ],
  // George Boole
  "e3428624-bd1e-48b9-ae8c-c66ba68879cc": [
    "warm_teacher",
    "wise_scholar",
    "soft_friendly",
  ],
  // William Playfair
  "ca6ebdca-5151-4a58-8523-56d25fce5dfa": [
    "soft_friendly",
    "warm_teacher",
    "genuine_happy",
  ],
  // Diophantus
  "78376372-3d71-4fce-8650-82b5be2c006b": [
    "wise_scholar",
    "warm_teacher",
    "neutral_alive",
  ],
  // Omar Khayyam
  "a2a43496-9a83-4444-813e-18a1930296e6": [
    "wise_scholar",
    "soft_friendly",
    "warm_teacher",
  ],
  // Benjamin Franklin
  "e617290b-bda0-4771-91ac-3fe6939dd412": [
    "warm_teacher",
    "genuine_happy",
    "soft_friendly",
  ],
  // Brahmagupta
  "2707ca40-df64-4258-97c8-67d3695e8a42": [
    "wise_scholar",
    "warm_teacher",
    "neutral_alive",
  ],
  // Leonhard Euler
  "5a39bed2-a551-4a03-98a2-46104a3324bf": [
    "warm_teacher",
    "genuine_happy",
    "soft_friendly",
  ],
  // Joseph-Louis Lagrange
  "c93478a1-ed2f-4f1a-81f6-3fa474a21566": [
    "wise_scholar",
    "soft_friendly",
    "warm_teacher",
  ],
  // Grace Hopper
  "94b6efbe-0aaa-47c7-bd3f-6d4f441d767f": [
    "warm_teacher",
    "genuine_happy",
    "soft_friendly",
  ],
  // Galileo Galilei
  "127cd993-f16e-4659-84fa-882cba633097": [
    "wise_scholar",
    "warm_teacher",
    "genuine_happy",
  ],
  // Carl Friedrich Gauss
  "7f61d092-99e4-48e6-bf80-7aa2b7bd98ff": [
    "wise_scholar",
    "warm_teacher",
    "neutral_alive",
  ],
  // Ada Lovelace
  "c0063a05-916b-45d0-bc2c-c63210414b17": [
    "soft_friendly",
    "warm_teacher",
    "elegant_soft",
  ],
  // Sigmund Freud
  "3841a6bb-f977-453d-92f5-6cec1267546f": [
    "neutral_alive",
    "wise_scholar",
    "soft_friendly",
  ],
  // Socrates
  "14836d13-ace6-4bf8-be26-26fbb80ed9bc": [
    "wise_scholar",
    "warm_teacher",
    "soft_friendly",
  ],
  // Al-Khwarizmi
  "6bcf3678-e232-46c9-996a-67beb3fd2dbf": [
    "wise_scholar",
    "warm_teacher",
    "soft_friendly",
  ],
  // Jacob Bernoulli
  "191e6103-a42c-4132-926c-230e7b1be5b6": [
    "wise_scholar",
    "warm_teacher",
    "soft_friendly",
  ],
  // Blaise Pascal
  "5802e813-e715-491d-a0fd-92dc643c458e": [
    "wise_scholar",
    "warm_teacher",
    "neutral_alive",
  ],
  // Srinivasa Ramanujan
  "f9f3f836-00c6-4160-8416-4b71b53f1307": [
    "soft_friendly",
    "warm_teacher",
    "wise_scholar",
  ],
  // Euclid
  "7c7ac397-c36e-4218-92d4-f437d1959cca": [
    "wise_scholar",
    "warm_teacher",
    "elegant_soft",
  ],
  // Richard Feynman
  "bc5d31a8-047b-4f72-8f16-fea7a365ca69": [
    "soft_friendly",
    "warm_teacher",
    "genuine_happy",
  ],
  // Marie Curie
  "73f610bf-d9c9-4d9f-a026-a10086a66725": [
    "elegant_soft",
    "polite",
    "tiny_natural",
  ],
  // Leonardo da Vinci
  "8944783c-2e2b-4796-b92e-21dacca776bc": [
    "wise_scholar",
    "soft_friendly",
    "elegant_soft",
  ],
  // Albert Einstein
  "278dddd6-b4a8-4303-a41d-be6219dbc907": [
    "wise_scholar",
    "warm_teacher",
    "genuine_happy",
  ],

  // Friedrich Nietzsche
  "88c314c4-5890-4e55-9418-8b46a376ef81a": [
    "wise_scholar",
    "warm_teacher",
    "genuine_happy",
    "polite",
  ],

  // Warren Buffett
  "44f87c0b-ca22-4225-9fea-8d823e6a0160": [
    "soft_friendly",
    "warm_teacher",
    "polite",
  ],
  // Henry Ford
  "5a16ff56-6e03-414e-a89d-cda9d83fae60": [
    "polite",
    "tiny_natural",
    "closed_mouth",
  ],
  // Genghis Khan
  "581e7293-43cf-45ea-9fb3-0955c563cbe2": [
    "neutral_alive",
    "tiny_natural",
    "smirk_right",
  ],
  // Frida Kahlo
  "4f50a698-6034-49c1-b9d4-221f59bf2378": [
    "warm_teacher",
    "soft_friendly",
    "tiny_natural",
  ],
  // Nostradamus
  "91d5cec5-1c21-423f-bbb4-bceb0f77a6c9": [
    "wise_scholar",
    "neutral_alive",
    "polite",
  ],
  // Niels Bohr
  "802bd089-d231-4c4e-830c-476199321af8": [
    "wise_scholar",
    "polite",
    "tiny_natural",
  ],
  // Confucius
  "b8951237-698b-42b5-8b6e-ee381b91fb65": [
    "elegant_soft",
    "wise_scholar",
    "polite",
  ],
  // Alan Turing
  "be0dcf0f-c72b-48f7-a462-6ba7560498d7": [
    "wise_scholar",
    "neutral_alive",
    "soft_friendly",
  ],
  // René Descartes
  "16f5b6a1-45f6-4c01-a612-60d4e4ab2297": [
    "wise_scholar",
    "elegant_soft",
    "warm_teacher",
  ],
  // Cyrus the Great
  "9ef81498-3fb9-4175-a6d4-c2a02d71c640": [
    "elegant_soft",
    "polite",
    "closed_mouth",
  ],
  // Mansa Musa
  "3f14429d-b2d4-4b99-8a05-58eed567c0d3": [
    "elegant_soft",
    "warm_teacher",
    "soft_friendly",
  ],
  // Niccolo Machiavelli
  "5a42d57c-6e2b-4ba8-bd32-838a1a5c5e6b": [
    "smirk_left",
    "polite",
    "tiny_natural",
  ],
  // Van Gogh
  "992e9007-b4f8-4711-80c2-884daf3d9960": [
    "neutral_alive",
    "tiny_natural",
    "closed_mouth",
  ],
  // Salvador Dali
  "d75e63e0-2b2b-43ef-933f-589e8d6818c0": [
    "smirk_right",
    "smirk_left",
    "genuine_happy",
  ],
  // Adam Smith
  "a59fe2c1-9046-405a-9a99-a7ddbaee13be": [
    "wise_scholar",
    "polite",
    "tiny_natural",
  ],
  // George Orwell
  "59c67a42-8c1e-45b8-aa41-b6fe6b3f5629": [
    "neutral_alive",
    "polite",
    "tiny_natural",
  ],
  // Muhammad Ali
  "2baa0479-7e38-4473-a2f2-1ad54149dbac": [
    "genuine_happy",
    "smirk_left",
    "soft_friendly",
  ],
  // Al Capone
  "e3f55c33-3ff0-471f-b833-b59d7b145fc6": [
    "smirk_left",
    "closed_mouth",
    "tiny_natural",
  ],
  // Karl Marx
  "4d7d2e17-b818-4144-b78b-629c688a71ac": [
    "neutral_alive",
    "polite",
    "tiny_natural",
  ],
};

/**
 * Resolve the preset key list for a character. Falls back to the
 * default list and filters out any keys that don't exist in the
 * preset library so a typo can't crash the controller.
 */
export function getIdleSmilePresetKeysForCharacter(modelId) {
  const list =
    (modelId && CHARACTER_SMILE_ASSIGNMENTS[modelId]) ||
    DEFAULT_IDLE_SMILE_PRESETS;
  const filtered = list.filter((k) => IDLE_SMILE_PRESETS[k]);
  return filtered.length > 0 ? filtered : DEFAULT_IDLE_SMILE_PRESETS.slice();
}
